import { createRoute, useNavigate } from '@tanstack/react-router'
import { getLocalTimeZone, today } from '@internationalized/date'
import { ArrowRight, Compass, Plus, Users } from 'lucide-react'
import rootRoute from './rootRoute'
import { Button, Row, SectionTitle } from '@/components/ui'
import EventCard from '@/components/EventCard'
import { $api } from '@/lib/fetch'
import { EventStatus } from '@/lib/constants'

function Hero() {
  const navigate = useNavigate()
  return (
    <section
      style={{
        position: 'relative',
        overflow: 'hidden',
        background: 'var(--surface-tint)',
        borderBottom: '1px solid var(--border-soft)',
      }}
    >
      <div
        aria-hidden="true"
        style={{
          position: 'absolute',
          inset: 0,
          backgroundImage: "url('/assets/zellij-tile.svg')",
          backgroundSize: '128px 128px',
          opacity: 0.5,
        }}
      />
      <div
        aria-hidden="true"
        style={{
          position: 'absolute',
          top: -120,
          right: -120,
          width: 420,
          height: 420,
          borderRadius: '50%',
          background:
            'radial-gradient(circle, rgba(205,169,78,0.18), transparent 70%)',
        }}
      />
      <div
        style={{
          position: 'relative',
          maxWidth: 'var(--container-max)',
          margin: '0 auto',
          padding: 'clamp(72px,11vw,128px) var(--gutter)',
          zIndex: 3,
        }}
      >
        <div style={{ maxWidth: 720 }}>
          <div className="oumma-overline" style={{ marginBottom: 16 }}>
            Assalamu alaikum — bienvenue
          </div>
          <h1
            style={{
              fontSize: 'var(--text-display)',
              lineHeight: 1.04,
              margin: '0 0 18px',
              color: 'var(--emerald-900)',
            }}
          >
            Trouvez les événements près de chez vous
          </h1>
          <p
            style={{
              fontSize: 'var(--text-lead)',
              color: 'var(--ink-2)',
              maxWidth: 560,
              margin: '0 0 30px',
              lineHeight: 1.55,
            }}
          >
            Cours, cercles, conférences et journées famille — chaque communauté,
            en un seul lieu accueillant.
          </p>
          <Button
            variant="primary"
            size="lg"
            iconLeft={<Compass size={18} />}
            onClick={() => navigate({ to: '/events', search: {} })}
          >
            Explorer les événements
          </Button>
        </div>
      </div>
    </section>
  )
}

function HowItWorks() {
  const steps = [
    {
      ic: <Compass size={24} />,
      t: 'Découvrez près de chez vous',
      d: 'Parcourez les événements de votre ville sur la carte et trouvez ce qui se passe cette semaine.',
    },
    {
      ic: <Users size={24} />,
      t: 'Rejoignez la communauté',
      d: 'Cours, cercles, collectes ou journées famille — il y a toujours un rassemblement pour vous.',
    },
    {
      ic: <Plus size={24} />,
      t: 'Proposez vos événements',
      d: 'Partagez votre événement avec toute la communauté en une minute.',
    },
  ]
  return (
    <section
      style={{
        maxWidth: 'var(--container-max)',
        margin: '0 auto',
        padding: 'var(--section-y) var(--gutter)',
      }}
    >
      <SectionTitle
        overline="Comment ça marche"
        title="Une communauté, trois gestes simples"
        align="center"
        style={{ marginBottom: 'var(--space-12)' }}
      />
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(260px,1fr))',
          gap: 24,
        }}
      >
        {steps.map((s, i) => (
          <div key={i} style={{ textAlign: 'center', padding: '8px 16px' }}>
            <div
              style={{
                width: 64,
                height: 64,
                borderRadius: '50%',
                margin: '0 auto 18px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: 'var(--emerald-50)',
                color: 'var(--emerald-700)',
                border: '1px solid var(--emerald-100)',
              }}
            >
              {s.ic}
            </div>
            <h3
              style={{
                fontSize: 'var(--text-h4)',
                margin: '0 0 8px',
                fontFamily: 'var(--font-display)',
              }}
            >
              {s.t}
            </h3>
            <p
              style={{
                fontSize: 15.5,
                color: 'var(--ink-2)',
                lineHeight: 1.6,
                maxWidth: 320,
                margin: '0 auto',
              }}
            >
              {s.d}
            </p>
          </div>
        ))}
      </div>
    </section>
  )
}

function ThisWeek() {
  const navigate = useNavigate()
  const { data } = $api.useQuery('post', '/api/events/search', {
    body: {
      status: EventStatus.Verified,
      min_date: today(getLocalTimeZone()).toString(),
    },
  })
  // Defensive guard: only surface validated events on the landing page.
  const events = (data?.events ?? [])
    .filter((e) => e.status === EventStatus.Verified)
    .slice(0, 3)
  if (events.length === 0) return null

  return (
    <section
      style={{
        background: 'var(--surface-raised)',
        borderTop: '1px solid var(--border-soft)',
        borderBottom: '1px solid var(--border-soft)',
      }}
    >
      <div
        style={{
          maxWidth: 'var(--container-max)',
          margin: '0 auto',
          padding: 'var(--section-y) var(--gutter)',
        }}
      >
        <Row
          justify="space-between"
          wrap
          gap="md"
          align="flex-end"
          style={{ marginBottom: 'var(--space-8)' }}
        >
          <SectionTitle
            overline="Cette semaine"
            title="À ne pas manquer près de chez vous"
          />
          <a
            href="/events"
            onClick={(e) => {
              e.preventDefault()
              navigate({ to: '/events', search: {} })
            }}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              color: 'var(--emerald-700)',
              fontWeight: 600,
              fontSize: 15,
            }}
          >
            Voir tous les événements <ArrowRight size={16} />
          </a>
        </Row>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(300px,1fr))',
            gap: 24,
          }}
        >
          {events.map((e) => (
            <EventCard key={e.id} event={e} />
          ))}
        </div>
      </div>
    </section>
  )
}

function HostCTA() {
  const navigate = useNavigate()
  return (
    <section
      style={{
        maxWidth: 'var(--container-max)',
        margin: '0 auto',
        padding: 'var(--section-y) var(--gutter)',
      }}
    >
      <div
        style={{
          position: 'relative',
          overflow: 'hidden',
          background: 'var(--surface-emerald)',
          borderRadius: 'var(--radius-xl)',
          padding: 'clamp(40px,6vw,72px)',
          textAlign: 'center',
          boxShadow: 'var(--shadow-lg)',
        }}
      >
        <div
          aria-hidden="true"
          style={{
            position: 'absolute',
            inset: 0,
            backgroundImage: "url('/assets/zellij-tile.svg')",
            backgroundSize: '120px 120px',
            opacity: 0.12,
          }}
        />
        <div style={{ position: 'relative' }}>
          <img
            src="/assets/oumma-mark-gold.svg"
            alt=""
            width="44"
            height="44"
            style={{ marginBottom: 18 }}
          />
          <h2
            style={{
              fontSize: 'var(--text-h2)',
              color: 'var(--paper-0)',
              margin: '0 0 12px',
            }}
          >
            Vous organisez un événement ?
          </h2>
          <p
            style={{
              fontSize: 'var(--text-lead)',
              color: 'var(--emerald-100)',
              maxWidth: 520,
              margin: '0 auto 28px',
            }}
          >
            Partagez-le avec la communauté. C'est gratuit, et il ne faut qu'une
            minute pour rassembler les gens.
          </p>
          <Button
            variant="gold"
            size="lg"
            iconLeft={<Plus size={18} />}
            onClick={() => navigate({ to: '/new-event' })}
          >
            Proposer un événement
          </Button>
        </div>
      </div>
    </section>
  )
}

const Home = () => (
  <div>
    <Hero />
    <HowItWorks />
    <ThisWeek />
    <HostCTA />
  </div>
)

export const homeRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/',
  component: Home,
})

export default Home
